import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useAppConfigContext } from '@/context/AppConfig';
import { AutomobileValuation } from '@/reports/types/vehicles';

const vendorURI = '/hk/dd/v2';
const queryKey = 'vehicleValuation';

interface VehicleValuationData {
  mileage?: number;
  region?: string;
  vin?: string;
  plate?: string;
  state?: string;
  defaultValuation?: AutomobileValuation;
}

const fetchVehicleValuation = async (params: VehicleValuationData) => {
  let URL = `${vendorURI}/vehicle/valuation?mileage=${params.mileage}&region=${params.region}`;
  if (params.vin && params.vin !== 'XXXXXXXXXXXXXXXXX') {
    URL = `${URL}&vin=${params.vin}`;
  } else {
    URL = `${URL}&license_plate=${params.plate}&state=${params.state}`;
  }
  try {
    const response = await fetch(URL);
    if (response.ok) {
      const data = await response.json();
      const valuation = data?.entities?.vehicles?.automobiles[0]?.valuation;
      return valuation;
    }
  } catch (error) {
    throw new Error('Error fetching vehicle valuation');
  }
};

export function useVehicleValuation(data: VehicleValuationData) {
  const queryClient = useQueryClient();
  const { logError } = useAppConfigContext();

  const result = useQuery({
    queryKey: [queryKey],
    queryFn: async () => fetchVehicleValuation(data),
    initialData: data.defaultValuation,
    staleTime: 10000,
    onError: (error) => {
      logError('Error fetching vehicle valuation', error as Error);
    },
  });

  const updateValuation = useMutation({
    mutationFn: (data: VehicleValuationData) => {
      return fetchVehicleValuation(data);
    },

    onSuccess: (data) => {
      queryClient.setQueryData([queryKey], data);
    },
    onError: (error) => {
      logError('Error updating valuation', error as Error);
    },
  });

  return { updateValuation, ...result };
}
